import Alpine from 'alpinejs';

const createFilter = (name, searchBoxInstance = null, config = {}) => {
  const filterConfig = {
    filterTypes: config.filterTypes || {},
    checkboxWrapperSelector: config.checkboxWrapperSelector || '',
    itemWrapperSelector: config.itemWrapperSelector || config.cardWrapperSelector || null,
    checkboxSelector: config.checkboxSelector || 'input[type="checkbox"]',
    filterTypeAttribute: config.filterTypeAttribute || 'data-filter-type',
    filterValueAttribute: config.filterValueAttribute || 'data-filter-value',
    itemSelector: config.itemSelector || 'div[id]',
  };

  const mergedFilterTypes = filterConfig.filterTypes;

  const callSearchProcess = function callSearchProcess() {
    if (typeof this.searchProcess === 'function') {
      this.searchProcess();
    }
  };

  const getSelectedFiltersArray = function getSelectedFiltersArray(filterType) {
    if (!this._selectedFilters) {
      this._selectedFilters = {};
    }
    if (!this._selectedFilters[filterType]) {
      this._selectedFilters[filterType] = [];
    }
    return this._selectedFilters[filterType];
  };

  const filterData = {
    name,
    _selectedFilters: {},
    toggleFilter(filterType, filterValue, event) {
      const checkbox = event.target.type === 'checkbox'
        ? event.target
        : event.target.querySelector(filterConfig.checkboxSelector);
      if (!checkbox) return;

      const array = getSelectedFiltersArray.call(this, filterType);

      if (checkbox.checked) {
        if (!array.includes(filterValue)) {
          array.push(filterValue);
        }
      } else {
        const index = array.indexOf(filterValue);
        if (index > -1) {
          array.splice(index, 1);
        }
      }

      callSearchProcess.call(this);

      Alpine.store('filters').count = this.getTotalFilterCount();
    },
    clearAllFilters() {
      Object.keys(mergedFilterTypes).forEach((filterType) => {
        const array = getSelectedFiltersArray.call(this, filterType);
        array.length = 0;
      });

      if (filterConfig.checkboxWrapperSelector) {
        const checkboxes = document.querySelectorAll(
          `${filterConfig.checkboxWrapperSelector} ${filterConfig.checkboxSelector}`,
        );
        for (let i = 0; i < checkboxes.length; i += 1) {
          checkboxes[i].checked = false;
        }
      }

      callSearchProcess.call(this);

      Alpine.store('filters').count = 0;
    },
    removeFilter(filterType, filterValue) {
      const array = getSelectedFiltersArray.call(this, filterType);

      const index = array.indexOf(filterValue);
      if (index > -1) {
        array.splice(index, 1);
      }

      const wrapper = filterConfig.checkboxWrapperSelector
        ? Array.from(document.querySelectorAll(filterConfig.checkboxWrapperSelector))
          .find((w) => w.getAttribute(filterConfig.filterTypeAttribute) === filterType
                  && w.getAttribute(filterConfig.filterValueAttribute) === filterValue)
        : null;
      if (wrapper) {
        const checkbox = wrapper.querySelector(filterConfig.checkboxSelector);
        if (checkbox) {
          checkbox.checked = false;
        }
      }

      callSearchProcess.call(this);
      
      Alpine.store('filters').count = Math.max(0, Alpine.store('filters').count - 1);
    },
    getAllSelectedFilters() {
      const filters = [];
      Object.keys(mergedFilterTypes).forEach((filterType) => {
        const array = getSelectedFiltersArray.call(this, filterType);
        array.forEach((value) => {
          filters.push({ type: filterType, value });
        });
      });
      return filters;
    },
    hasActiveFilters() {
      return Object.keys(mergedFilterTypes).some((filterType) => {
        const array = getSelectedFiltersArray.call(this, filterType);
        return array.length > 0;
      });
    },
    getFilterCount(filterType) {
      const array = getSelectedFiltersArray.call(this, filterType);
      return array.length;
    },
    getTotalFilterCount() {
      const filters = this.getAllSelectedFilters();
      return filters ? filters.length : 0;
    },
    matchesFilters(itemId) {
      const itemElement = document.getElementById(itemId);
      if (!itemElement) return true;

      const wrapper = filterConfig.itemWrapperSelector
        ? itemElement.querySelector(filterConfig.itemWrapperSelector)
        : itemElement;
      if (!wrapper) return true;

      return Object.keys(mergedFilterTypes).every((filterType) => {
        const filterTypeConfig = mergedFilterTypes[filterType];
        const array = getSelectedFiltersArray.call(this, filterType);

        if (array.length === 0) {
          return true;
        }

        const itemValue = (wrapper.getAttribute(filterTypeConfig.attribute) || '').toLowerCase();

        if (filterTypeConfig.separator) {
          const separator = filterTypeConfig.separator;
          const valueParts = itemValue.split(separator).map((part) => part.trim());
          return array.every((selectedValue) => valueParts.includes(selectedValue.toLowerCase()));
        }

        return array.some((selectedValue) => selectedValue.toLowerCase() === itemValue);
      });
    },
    countVisibleInAccordion(panelId) {
      const panel = document.getElementById(panelId);
      if (!panel) return 0;

      const itemSelector = filterConfig.itemSelector || 'div[id]';
      const items = panel.querySelectorAll(itemSelector);
      let count = 0;

      items.forEach((item) => {
        if (item.id && this.isVisible && typeof this.isVisible === 'function' && this.isVisible(item.id)) {
          count += 1;
        }
      });

      return count;
    },
    getProjectCountText(panelId) {
      const count = this.countVisibleInAccordion(panelId);

      if (count === 0) {
        return '';
      }

      const text = count === 1 ? 'project' : 'projects';
      return `${count} ${text}`;
    },
  };

  if (!searchBoxInstance) {
    return filterData;
  }

  const combinedData = {};
  Object.defineProperties(combinedData, Object.getOwnPropertyDescriptors(searchBoxInstance));
  Object.defineProperties(combinedData, Object.getOwnPropertyDescriptors(filterData));

  combinedData.refreshSearch = function refreshSearch(pageNumber = 1) {
    this.pageNumber = pageNumber;
    this.searchProcess();
  };

  combinedData.isVisible = function isVisible(id) {
    const hasFilters = Object.keys(mergedFilterTypes).some((filterType) => {
      const array = getSelectedFiltersArray.call(this, filterType);
      return array.length > 0;
    });
    const foundItem = this.items.find((item) => item.id === id);

    if (!hasFilters) {
      if (foundItem) {
        return foundItem.visible !== undefined ? !!foundItem.visible : true;
      }
      return !this.isSearching;
    }

    if (this.isSearching && (!foundItem || foundItem.visible === false)) {
      return false;
    }

    return this.matchesFilters(id);
  };

  Object.defineProperty(combinedData, 'numberOfResults', {
    get() {
      const searchResults = this.items.filter((item) => item.enable);
      const hasFilters = Object.keys(mergedFilterTypes).some((filterType) => {
        const array = getSelectedFiltersArray.call(this, filterType);
        return array.length > 0;
      });

      if (hasFilters) {
        return searchResults.filter((item) => this.matchesFilters(item.id)).length;
      }
      return searchResults.length;
    },
    enumerable: true,
    configurable: true,
  });

  combinedData.numberOfItemsShown = function numberOfItemsShown() {
    const searchResults = this.items.filter((item) => item.enable);
    const hasFilters = Object.keys(mergedFilterTypes).some((filterType) => {
      const array = getSelectedFiltersArray.call(this, filterType);
      return array.length > 0;
    });
    const filteredItems = hasFilters
      ? searchResults.filter((item) => this.matchesFilters(item.id))
      : searchResults;

    const totalFiltered = filteredItems.length;
    const paginationLimit = this.pageNumber * this.pageSize;
    return totalFiltered > paginationLimit ? paginationLimit : totalFiltered;
  };

  combinedData.hasActiveFilters = function hasActiveFilters() {
    if (this.isSearching) {
      return true;
    }
    return Object.keys(mergedFilterTypes).some((filterType) => {
      const array = getSelectedFiltersArray.call(this, filterType);
      return array.length > 0;
    });
  };

  combinedData.getFilterCount = function getFilterCount(filterType) {
    const array = getSelectedFiltersArray.call(this, filterType);
    return array.length;
  };

  combinedData.countVisibleInAccordion = function countVisibleInAccordion(panelId) {
    const panel = document.getElementById(panelId);
    if (!panel) return 0;

    const itemSelector = filterConfig.itemSelector || 'div[id]';
    const items = panel.querySelectorAll(itemSelector);
    let count = 0;

    items.forEach((item) => {
      if (item.id && this.isVisible(item.id)) {
        count += 1;
      }
    });

    return count;
  };

  return combinedData;
};

export default createFilter;
